/**
 * SynthesisProgressLine — tiny inline status line shown under the
 * Publish button while a chapter's audio is synthesising in the
 * background. Hidden when there's no active job.
 *
 * Drive it from the parent's state:
 *   const [progress, setProgress] = useState(null);
 *   pollChapterSynthesis({
 *     kind, id,
 *     onProgress: (p) => setProgress(p),  // {elapsedMs, status, attempt}
 *   })
 *   .then(()  => setProgress(null))
 *   .catch(() => setProgress(null));
 *
 *   <SynthesisProgressLine progress={progress} />
 */
import React from 'react';
import { Loader2, AlertTriangle } from 'lucide-react';

const fmtElapsed = (ms) => {
  const s = Math.floor((ms || 0) / 1000);
  const mm = Math.floor(s / 60);
  const ss = s % 60;
  return `${mm}:${String(ss).padStart(2, '0')}`;
};

const SynthesisProgressLine = ({ progress }) => {
  if (!progress) return null;

  const isBlip = progress.status === 'network-blip';
  // Upstream `/generate-long` reports progress as a "done/total" string
  // (e.g., "3/11"). Fall back to legacy per-chunk-job counts when the
  // upstream string is missing.
  const upstreamProgress = typeof progress.progress === 'string' ? progress.progress : null;
  const hasChunks = typeof progress.chunks_total === 'number' && progress.chunks_total > 0;
  const label = isBlip
    ? 'Network blip — retrying…'
    : upstreamProgress
      ? `Synthesising audio… ${upstreamProgress} chunks · ${fmtElapsed(progress.elapsedMs)} elapsed`
      : hasChunks
        ? `Synthesising audio… ${progress.chunks_done}/${progress.chunks_total} chunks · ${fmtElapsed(progress.elapsedMs)} elapsed`
        : `Synthesising audio… ${fmtElapsed(progress.elapsedMs)} elapsed (poll ${progress.attempt})`;

  return (
    <div
      className="mt-2 rounded-md border border-fuchsia-500/40 bg-fuchsia-500/10 px-3 py-2 flex items-center gap-2 text-[12px] text-fuchsia-100"
      data-testid="synthesis-progress-line"
    >
      {isBlip ? (
        <AlertTriangle className="w-3.5 h-3.5 text-amber-300 shrink-0" />
      ) : (
        <Loader2 className="w-3.5 h-3.5 text-fuchsia-300 animate-spin shrink-0" />
      )}
      <span className="font-mono tabular-nums">{label}</span>
    </div>
  );
};

export default SynthesisProgressLine;
